import { InputWithLabel } from '@adotel/components'
import {
  Button,
  CircularProgress,
  Modal,
  Paper,
  Slide,
  Typography
} from '@material-ui/core'
import { ReactNode, useState } from 'react'

import { getDirection, getTranslate } from '../../localization'
import useStyles from './index.styles'

export interface ConfirmActionModalProps {
  /**
   * icon to show in modal
   */
  icon: ReactNode
  /**
   * To show main text
   */
  /**
   * Accepts a node so a caller can wrap a Latin identifier in <bdi> — the bidi
   * algorithm reorders "POLICY-1 v2" inside an RTL dialog otherwise. A plain
   * string still goes through getTranslate, so the nine existing callers are
   * unaffected.
   */
  title: React.ReactNode
  /**
   * To show sub text
   */
  subtitle: string
  /**
   * Text to show in input box
   */
  signature: string
  /**
   * For control modal be open or not
   */
  isOpen: boolean
  /**
   * A function for closing modal
   */
  onClose: () => void
  /**
   * What Happen When Click On Button
   */
  onClick: () => void
  /**
   * Set loading for diable button
   */
  loading: boolean
}

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  icon,
  title,
  subtitle,
  signature,
  isOpen,
  onClose,
  onClick,
  loading
}) => {
  const classes = useStyles()
  const [input, setInput] = useState('')

  return (
    <Modal open={isOpen} onClose={onClose} dir={getDirection()}>
      <Slide direction="down" in={isOpen}>
        <Paper
          className={classes.deletePaper}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-action-title">
          <div className={classes.header}>
            <div>{icon}</div>
            <Typography
              id="confirm-action-title"
              variant="h3"
              className={classes.space}>
              {typeof title === 'string' ? getTranslate(title) : title}
            </Typography>
            <Typography variant="h5" className={classes.space}>
              {getTranslate(subtitle)}
            </Typography>
            <div className={classes.inputWithLable}>
              <InputWithLabel
                label={getTranslate('Confirmation signature')}
                id="confirmation-signature"
                name="confirmation-signature"
                value={input}
                onChange={(e) => {
                  setInput(e.currentTarget.value)
                }}
                placeholder={`${getTranslate('Please Type:')} ${signature}`}
                inputSize={'small'}
              />
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            <Button variant="text" color="primary" onClick={onClose}>
              {getTranslate('Cancel')}
            </Button>
            {/*
              `!signature.trim()` is load-bearing, not defensive padding.

              `input` starts as '', so with an empty or whitespace-only signature the comparison
              below is '' !== '' — false — and Confirm is ENABLED the moment the modal opens,
              with nothing typed. The typed-confirmation gate silently disarms itself exactly
              when it is asked to guard a record whose name is blank.

              It had never been reachable: the other eight call sites pass literals
              ('Delete Me', 'Archive Me', …) that cannot be empty. The promotions row is the
              first caller to pass a DATA-derived signature (the promotion's own name), which is
              why the latent case became worth closing. It is not reachable through the
              promotion form either — PromotionBuilder rejects `!form.name.trim()` — so this
              guards records created by any other path, and any future dynamic caller.

              Deliberately NOT also trimming `input`: that would LOOSEN the gate for all nine
              callers (accepting 'Delete Me ' for 'Delete Me'), which is a separate decision from
              closing a hole and does not belong in the same change.
            */}
            <Button
              disabled={
                loading ||
                !signature.trim() ||
                input.toLowerCase() !== signature.toLowerCase()
              }
              variant="contained"
              className={classes.deleteButton}
              onClick={onClick}
              color="primary">
              {getTranslate('Confirm')}
              {loading && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                />
              )}
            </Button>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}
